import { useState } from "react";
import { Link, useParams } from "wouter";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { Bell, BellOff, Calendar, MapPin, Trash2 } from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer";
import { Button } from "../components/ui/button";
import type { Id } from "convex/_generated/dataModel";
import { formatTime } from "@/lib/dateUtils";

/** Shape returned per reminder by `reminders.getByToken`. */
type ReminderRow = {
  _id: string;
  remindAt: number;
  event: {
    _id: string;
    title: string;
    date: string;
    startTime: string;
    venueName: string;
  };
};

/**
 * Reached only from a tokenised link in a reminder email, so it must work with
 * no account and no Clerk — it is registered in both AuthRoutes and
 * NoAuthRoutes. The token in the URL is the credential.
 */
export function ManageRemindersPage() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";

  const data = useQuery(api.reminders.getByToken, token ? { token } : "skip");
  const cancelReminder = useMutation(api.reminders.cancelReminder);
  const unsubscribeAll = useMutation(api.reminders.unsubscribeAll);
  const [busy, setBusy] = useState<string | null>(null);

  const formatWhen = (date: string, startTime: string) => {
    const parsed = new Date(`${date}T00:00:00Z`);
    const day = Number.isNaN(parsed.getTime())
      ? date
      : parsed.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        });
    return `${day} · ${formatTime(startTime)}`;
  };

  if (!token || data === null) {
    return (
      <PageContainer>
        <div className="max-w-lg mx-auto py-16 text-center">
          <BellOff className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-foreground mb-2">
            This link isn't valid
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            It may have already been used to unsubscribe, or the link was
            truncated by your email client.
          </p>
          <Button asChild>
            <Link href="/events">Browse events</Link>
          </Button>
        </div>
      </PageContainer>
    );
  }

  if (data === undefined) {
    return (
      <PageContainer>
        <div className="max-w-lg mx-auto py-12 space-y-3">
          <div className="h-8 w-48 rounded bg-muted animate-pulse" />
          <div className="h-20 rounded-lg bg-muted animate-pulse" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-lg mx-auto py-10">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Your reminders
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            For <span className="text-foreground">{data.email}</span>
          </p>
        </header>

        {data.unsubscribed && (
          <div className="mb-6 p-4 rounded-lg border border-border bg-muted">
            <p className="text-sm text-foreground font-medium mb-1">
              You're unsubscribed
            </p>
            <p className="text-sm text-muted-foreground">
              We won't email this address again. You can still set a new
              reminder from any event page whenever you like.
            </p>
          </div>
        )}

        {data.reminders.length === 0 ? (
          <div className="p-6 rounded-lg border border-border bg-card text-center">
            <p className="text-sm text-muted-foreground mb-4">
              No reminders scheduled.
            </p>
            <Button asChild variant="outline">
              <Link href="/events">Find something to dance to</Link>
            </Button>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {(data.reminders as ReminderRow[]).map((r) => (
              <li
                key={r._id}
                className="flex items-start justify-between gap-4 p-4 rounded-lg border border-border bg-card"
              >
                <div className="min-w-0">
                  <p className="font-medium text-foreground">
                    {r.event.title}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 shrink-0" />
                    {formatWhen(r.event.date, r.event.startTime)}
                  </p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    {r.event.venueName}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy === r._id}
                  onClick={async () => {
                    setBusy(r._id);
                    try {
                      await cancelReminder({
                        token,
                        reminderId: r._id as Id<"eventReminders">,
                      });
                    } finally {
                      setBusy(null);
                    }
                  }}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                  {busy === r._id ? "..." : "Cancel"}
                </Button>
              </li>
            ))}
          </ul>
        )}

        {!data.unsubscribed && (
          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground mb-3">
              Want an account? You'd get all your reminders in one place, plus
              favourite studios and styles — and alerts for new events near you,
              coming soon.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm">
                <Link href="/signup">Create an account</Link>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy === "all"}
                onClick={async () => {
                  setBusy("all");
                  try {
                    await unsubscribeAll({ token });
                  } finally {
                    setBusy(null);
                  }
                }}
                className="text-muted-foreground hover:text-destructive"
              >
                <BellOff className="w-4 h-4" />
                {busy === "all" ? "Unsubscribing..." : "Unsubscribe from all"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
